import React from 'react';
import { Check, Gift, ArrowLeft } from 'lucide-react';

// ── Skeleton (loading placeholder block) ─────────────────────────────
export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse rounded-md bg-black/[0.06] ${className}`} />
);

// ── Back link (consistent "← label" affordance for the flow steps) ───
export const BackLink: React.FC<{ label: string; onClick: () => void; className?: string }> = ({
  label,
  onClick,
  className = '',
}) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1 text-[13px] text-[#6b6b6b] transition-colors hover:text-[#111] ${className}`}
  >
    <ArrowLeft className="h-4 w-4" /> {label}
  </button>
);

/**
 * Flat marketplace primitives that mirror the breakfast handoff mockups
 * (white cards, hairline 0.5px borders, no shadow/lift, 8–12px radii, medium
 * type, muted greys) — with our teal brand accent swapped in for the mockup's
 * blue. Kept separate from the enterprise `Card`/`Button` so the design system
 * is untouched.
 *
 * Mockup token map:
 *   --border      rgba(0,0,0,0.12)  →  border-black/10
 *   --border-md   rgba(0,0,0,0.22)  →  border-black/20
 *   --bg-2        #f5f5f3
 *   --blue        #1B4F8A/#185FA5   →  primary (#07E5D1)  [teal swap]
 *   green         #3B6D11/#EAF3DE   →  emerald (success ticks)
 */

// ── Card ─────────────────────────────────────────────────────────────
export const MktCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  selected?: boolean;
  onClick?: () => void;
}> = ({ children, className = '', selected = false, onClick }) => (
  <div
    onClick={onClick}
    className={[
      'bg-white rounded-xl border transition-colors',
      selected ? 'border-primary bg-[#E8FCFA]' : 'border-black/10',
      onClick ? 'cursor-pointer hover:border-black/20' : '',
      className,
    ].join(' ')}
  >
    {children}
  </div>
);

// ── Button ───────────────────────────────────────────────────────────
type MktButtonVariant = 'primary' | 'secondary' | 'ghost';
export const MktButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: MktButtonVariant; fullWidth?: boolean }
> = ({ variant = 'primary', fullWidth = false, className = '', children, disabled, ...props }) => {
  const variants: Record<MktButtonVariant, string> = {
    primary: disabled
      ? 'bg-primary/40 text-white cursor-not-allowed'
      : 'bg-primary text-white hover:bg-[#06D4C2]',
    secondary: 'border border-black/20 text-[#111] bg-transparent hover:bg-[#f5f5f3]',
    ghost: 'text-[#6b6b6b] hover:text-[#111] bg-transparent',
  };
  return (
    <button
      disabled={disabled}
      className={[
        'inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium px-5 py-2.5 transition-colors',
        variants[variant],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
      {...props}
    >
      {children}
    </button>
  );
};

// ── Eyebrow (uppercase tracked label) ────────────────────────────────
export const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`text-[11px] font-medium uppercase tracking-[0.05em] text-[#6b6b6b] ${className}`}>
    {children}
  </div>
);

// ── Sub-category pill ────────────────────────────────────────────────
export const Pill: React.FC<{
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  showCheck?: boolean;
}> = ({ active = false, onClick, children, showCheck = false }) => (
  <button
    onClick={onClick}
    className={[
      'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] whitespace-nowrap transition-colors border',
      active
        ? 'bg-primary text-white border-primary'
        : 'bg-white text-[#6b6b6b] border-black/20 hover:border-black/40',
    ].join(' ')}
  >
    {active && showCheck && <Check className="h-3 w-3" />}
    {children}
  </button>
);

// ── Teal mini tag ────────────────────────────────────────────────────
export const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-[#E8FCFA] px-2 py-0.5 text-[11px] font-medium text-[#0B7C74]">
    {children}
  </span>
);

// ── Checkbox (16px) ──────────────────────────────────────────────────
export const Checkbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={[
      'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border-[1.5px] transition-colors',
      checked ? 'bg-primary border-primary' : 'border-black/20 bg-white',
    ].join(' ')}
  >
    {checked && <Check className="h-2.5 w-2.5 text-white" />}
  </span>
);

// ── Free badge (emerald) ─────────────────────────────────────────────
export const FreeBadge: React.FC<{ children?: React.ReactNode }> = ({
  children = '1 free study remaining',
}) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
    <Gift className="h-3 w-3" />
    {children}
  </span>
);

// ── Price tag (right-aligned $5 / Twin block) ────────────────────────
export const PriceTag: React.FC<{ amount?: string; unit?: string }> = ({
  amount = '$5',
  unit = 'Price per Twin',
}) => (
  <div className="text-right flex-shrink-0">
    <div className="text-[12px] text-[#6b6b6b]">{unit}</div>
    <div className="text-[20px] font-medium text-[#111]">{amount}</div>
  </div>
);

// ── Study status badge ───────────────────────────────────────────────
// Centralises status labels so chat (interactive, never "running") and the
// sim use-cases read correctly across the dashboard + history list.
export interface StudyLike {
  use_case?: string;
  status?: string;
  report_status?: string;
  queries_used?: number | null;
  queries_allowed?: number | null;
  queries_remaining?: number | null;
}

export function studyStatus(s: StudyLike): { label: string; cls: string } {
  const emerald = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const teal = 'bg-[#E8FCFA] text-[#0B7C74] border-[#07E5D1]/30';
  const amber = 'bg-amber-50 text-amber-700 border-amber-200';
  const red = 'bg-red-50 text-red-700 border-red-200';
  const gray = 'bg-gray-50 text-gray-600 border-gray-200';

  if (s.use_case === 'chat') {
    // Chat is interactive — it is "Active" until the query budget is spent.
    if (s.status === 'pending_payment') return { label: 'Awaiting payment', cls: teal };
    if (s.queries_allowed != null && (s.queries_remaining ?? 0) <= 0) return { label: 'Complete', cls: gray };
    return { label: 'Active', cls: teal };
  }
  if (s.status === 'report_ready' || s.report_status === 'ready') return { label: 'Ready', cls: emerald };
  if (s.status === 'pending_payment') return { label: 'Awaiting payment', cls: teal };
  if (s.status === 'failed' || s.report_status === 'failed') return { label: 'Failed', cls: red };
  if (s.status === 'running') return { label: 'In progress', cls: amber };
  return { label: (s.status || '').replace(/_/g, ' ') || '—', cls: gray };
}

export const StatusBadge: React.FC<{ study: StudyLike }> = ({ study }) => {
  const { label, cls } = studyStatus(study);
  return <span className={`rounded-md border px-2 py-1 text-[11px] capitalize ${cls}`}>{label}</span>;
};

// Where opening a study from history/dashboard should go. Status wins over
// use-case: an unpaid study resumes checkout (Review) rather than dead-ending on
// a chat/results screen it can't show.
export function studyHref(study: StudyLike, id: number): { path: string; state?: { studyId: number } } {
  if (study.status === 'pending_payment') {
    return { path: '/marketplace/run/review', state: { studyId: id } };
  }
  if (study.use_case === 'chat') return { path: `/marketplace/studies/${id}/chat` };
  return { path: `/marketplace/studies/${id}/results` };
}

// ── Emerald check row (benefit list item) ────────────────────────────
export const CheckRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <Check className="mt-0.5 h-[15px] w-[15px] flex-shrink-0 text-emerald-600" />
    <span className="text-[12px] text-[#6b6b6b] leading-snug">{children}</span>
  </div>
);
